import React, { useEffect } from 'react'
import { useScanEvents } from '../../upload/hooks/useScanEvents'
import { useScanPanel } from '../contexts/ScanPanelContext'
import { getFreshAccessToken } from '../../../lib/api/api'
import { isOwnScanEvent } from '../previewSource'
import ScanPanel from './ScanPanel'

const ACK_KEY = 'nadia_acked_scan_ids'
// Catch-up window: covers scans that landed while the SSE subscriber was down
// (uvicorn reload, network drop, tab sleep, user away from the Mac). Must be
// wide enough to recover from a realistic pause, narrow enough not to bring
// back yesterday's scans. Server-side caps this at 120 minutes.
const CATCHUP_MINUTES = 60

function readAcked(): Set<string> {
  try {
    const raw = sessionStorage.getItem(ACK_KEY)
    if (!raw) return new Set()
    return new Set(JSON.parse(raw) as string[])
  } catch {
    return new Set()
  }
}

function writeAcked(set: Set<string>): void {
  try {
    sessionStorage.setItem(ACK_KEY, JSON.stringify(Array.from(set)))
  } catch {
    // ignore quota errors
  }
}

function markAcked(scan_id: string): void {
  const set = readAcked()
  if (set.has(scan_id)) return
  set.add(scan_id)
  writeAcked(set)
}

type RecentScan = {
  scan_id: string
  file_name: string
  preview_url: string
  received_at: string
  status: 'received' | 'complete' | 'error'
  doc_id: number | null
  title: string
  description: string
  doc_type: string
  error: string | null
}

/**
 * Headless mount: subscribes to scan SSE events and pushes them into the
 * ScanPanelContext. Also runs a catch-up fetch at mount to recover scans
 * received while SSE was disconnected (uvicorn reload, network drop, tab
 * sleep). Already-seen scan IDs are deduped via sessionStorage.
 *
 * The backend only streams events of offices the user may view; the panel
 * keeps just the user's own scans (`own`), so a colleague's upload never
 * pops up here.
 */
export default function ScanPanelMount() {
  const { addScan, updateScan, setVisionBatch, setVisionPage } = useScanPanel()

  // Catch-up at mount: pull any recent scans we might have missed
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const token = await getFreshAccessToken()
      if (!token || cancelled) return
      try {
        const resp = await fetch(
          `/api/v1/documents/scan-upload/recent?minutes=${CATCHUP_MINUTES}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        )
        if (!resp.ok || cancelled) {
          if (!resp.ok) console.warn('[scan catch-up] response not ok', resp.status)
          return
        }
        const data = (await resp.json()) as { scans: RecentScan[] }
        const acked = readAcked()
        for (const s of data.scans) {
          if (acked.has(s.scan_id)) continue
          addScan({
            scan_id: s.scan_id,
            file_name: s.file_name,
            preview_url: s.preview_url,
            status: s.status === 'complete' ? 'received' : s.status,
            received_at: s.received_at,
          })
          if (s.status === 'complete') {
            updateScan(s.scan_id, {
              status: 'complete',
              doc_id: s.doc_id != null ? String(s.doc_id) : null,
              title: s.title,
              description: s.description,
              doc_type: s.doc_type,
              error: null,
            })
          }
          markAcked(s.scan_id)
        }
      } catch {
        // catch-up is best-effort; live SSE still works
      }
    })()
    return () => {
      cancelled = true
    }
  }, [addScan, updateScan])

  useScanEvents({
    onReceived: (e) => {
      if (!isOwnScanEvent(e)) return
      addScan({
        scan_id: e.scan_id,
        file_name: e.file_name,
        preview_url: e.preview_url,
        status: 'received',
        received_at: e.received_at,
      })
      markAcked(e.scan_id)
    },
    onComplete: (e) => {
      if (!isOwnScanEvent(e)) return
      updateScan(e.scan_id, {
        status: e.error ? 'error' : 'complete',
        doc_id: e.doc_id,
        title: e.title,
        description: e.description,
        doc_type: e.doc_type,
        error: e.error,
      })
      markAcked(e.scan_id)
    },
    onProgress: (e) => {
      if (!isOwnScanEvent(e)) return
      updateScan(e.scan_id, {
        stage: e.stage,
        stage_label: e.stage_label,
        total_pages: e.total_pages,
        chunks_total: e.chunks_total,
        chunks_embedded: e.chunks_embedded,
      })
      markAcked(e.scan_id)
    },
    onVisionPage: (e) => {
      if (!isOwnScanEvent(e)) return
      if (e.event === 'batch_start') {
        setVisionBatch(e.scan_id, e.total_pages)
      } else if (e.event === 'start') {
        setVisionPage(e.scan_id, e.page, 'processing')
      } else if (e.event === 'complete') {
        setVisionPage(e.scan_id, e.page, 'done')
      }
      markAcked(e.scan_id)
    },
  })

  return <ScanPanel />
}
